import React, { useEffect, useMemo, useState } from 'react';
import ReactSwitch from 'react-switch';
import { cloneDeep } from 'lodash';
import { Button, WebhookUrlList } from 'src/components';
import { useAlerts } from 'src/common/AlertManager';
import { useUpdateAdminUserSettingsMutation } from 'src/api/mutations/adminUserSettingsMutations';

import styles from '../styles.module.scss';

interface WebhookTabProps {
  userId: number;
  userSettings: any;
}

const WebhookTab: React.FC<WebhookTabProps> = ({ userId, userSettings }) => {
  const { showSuccess, showAlert } = useAlerts();
  const updateSettingsMutation = useUpdateAdminUserSettingsMutation(userId);

  const serverWebhook = userSettings?.webhook;
  const [local, setLocal] = useState<any>(() => cloneDeep(serverWebhook) || {});

  useEffect(() => {
    if (serverWebhook) setLocal(cloneDeep(serverWebhook));
  }, [serverWebhook]);

  const hasChanges = useMemo(
    () => JSON.stringify(local) !== JSON.stringify(serverWebhook),
    [local, serverWebhook],
  );

  const events = useMemo(() => {
    if (!local.callback_events) return [];
    return Object.entries(local.callback_events).map(([key, value]: [string, any]) => ({
      key,
      enabled: value,
    }));
  }, [local.callback_events]);

  const handleEventChange = (name: string, value: boolean) => {
    setLocal((prev: any) => ({
      ...prev,
      callback_events: { ...prev.callback_events, [name]: value },
    }));
  };

  const handleSave = async () => {
    try {
      await updateSettingsMutation.mutateAsync({ key: 'webhook', value: local });
      showSuccess('Webhook settings saved');
    } catch {
      showAlert('Failed to save webhook settings');
    }
  };

  if (!serverWebhook) {
    return <div className={styles.emptyState}>No webhook settings configured for this user.</div>;
  }

  return (
    <div>
      <h4 className={styles.settingsSectionTitle}>Webhooks</h4>
      <div className={styles.inputSection}>
        <WebhookUrlList
          value={local.web_urls}
          onChange={(value: string) => setLocal((prev: any) => ({ ...prev, web_urls: value }))}
        />
      </div>

      <div className={styles.webhookOptions}>
        <h4 className={styles.webhookOptionsTitle}>Asset events</h4>
        {events.map(({ key, enabled }) => (
          <div key={key} className={styles.webhookOption}>
            <ReactSwitch
              checked={enabled}
              onChange={(value) => handleEventChange(key, value)}
              checkedIcon={false}
              uncheckedIcon={false}
              onColor="#2e53e3"
            />
            <label>{key}</label>
          </div>
        ))}
      </div>

      <div className={styles.settingsActions}>
        <Button
          mode="primary"
          size="small"
          onClick={handleSave}
          disabled={!hasChanges || updateSettingsMutation.isLoading}
        >
          Save Webhook Settings
        </Button>
      </div>
    </div>
  );
};

export default WebhookTab;
